import {
	Crown,
	Loader2,
	Medal,
	Search,
	TrendingUp,
	Trophy,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { cn } from "@/lib/utils";
import IdentityButton from "../components/IdentityButton";
import IdentityList from "../components/IdentityList";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../components/ui/tabs";
import { useRewardsLeaderboard } from "../hooks/useRewardsLeaderboard";

// Rank badge component for top 3
function RankBadge({ rank }: { rank: number }) {
	if (rank === 1) {
		return (
			<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-linear-to-br from-yellow-400/20 to-yellow-600/20 ring-1 ring-yellow-400/30">
				<Crown className="w-4 h-4 text-yellow-400" />
			</div>
		);
	}
	if (rank === 2) {
		return (
			<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-linear-to-br from-slate-300/20 to-slate-400/20 ring-1 ring-slate-300/30">
				<Medal className="w-4 h-4 text-slate-300" />
			</div>
		);
	}
	if (rank === 3) {
		return (
			<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-linear-to-br from-amber-500/20 to-amber-700/20 ring-1 ring-amber-500/30">
				<Medal className="w-4 h-4 text-amber-500" />
			</div>
		);
	}
	return (
		<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg-tertiary/50 text-text-tertiary font-mono text-sm font-semibold">
			{rank}
		</div>
	);
}

export default function LeaderboardPage() {
	const {
		data: leaderboard = [],
		isLoading: isLeaderboardLoading,
		error: leaderboardQueryError,
	} = useRewardsLeaderboard();
	const leaderboardError = leaderboardQueryError
		? leaderboardQueryError instanceof Error
			? leaderboardQueryError.message
			: "Failed to load leaderboard"
		: null;

	// Staking search state
	const [stakingFilter, setStakingFilter] = useState("");
	const [debouncedStakingFilter, setDebouncedStakingFilter] = useState("");

	const debounceStakingFilterCallback = useCallback((value: string) => {
		setDebouncedStakingFilter(value);
	}, []);

	const debounceStakingFilter = useDebounceCallback(
		debounceStakingFilterCallback,
		500,
	);

	const handleStakingFilterChange = (value: string) => {
		setStakingFilter(value);
		debounceStakingFilter(value);
	};

	const sortedLeaderboard = useMemo(
		() => [...leaderboard].sort((a, b) => b.points - a.points),
		[leaderboard],
	);

	return (
		<main className="grow pb-20 px-6 font-inter">
			<div className="max-w-4xl mx-auto space-y-8">
				{/* Header with integrated tabs */}
				<Tabs defaultValue="rewards" className="w-full">
					<div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
						<div className="space-y-2">
							<h1 className="font-clash text-4xl font-medium text-text-default tracking-tight">
								Leaderboard
							</h1>
							<p className="text-sm text-text-secondary max-w-md">
								Track the top contributors across the Shinkai network
							</p>
						</div>

						<TabsList className="grid grid-cols-2 w-full sm:w-auto sm:min-w-[280px] p-1  border border-divider rounded-xl">
							<TabsTrigger
								value="rewards"
								className="font-medium bg-transparent gap-2 data-[state=active]:bg-bg-secondary data-[state=active]:shadow-sm rounded-lg transition-all text-sm"
							>
								<Trophy className="w-4 h-4" />
								Rewards
							</TabsTrigger>
							<TabsTrigger
								value="staking"
								className="font-medium bg-transparent gap-2 data-[state=active]:bg-bg-secondary data-[state=active]:shadow-sm rounded-lg transition-all text-sm"
							>
								<TrendingUp className="w-4 h-4" />
								Staking
							</TabsTrigger>
						</TabsList>
					</div>

					{/* Rewards Leaderboard */}
					<TabsContent value="rewards" className="mt-0">
						<div className="rounded-2xl border border-divider bg-bg-secondary/20 backdrop-blur-sm overflow-hidden">
							{/* Header */}
							<div className="flex items-center justify-between px-5 py-4 border-b border-divider bg-bg-tertiary/30">
								<div className="flex items-center gap-3">
									<div className="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center">
										<Trophy className="w-4 h-4 text-amber-400" />
									</div>
									<div>
										<h2 className="font-medium text-text-default">
											Rewards Points
										</h2>
										<p className="text-xs text-text-tertiary">
											Earned from completed challenges
										</p>
									</div>
								</div>
								<div className="text-right">
									<div className="text-lg font-semibold text-text-default tabular-nums">
										{sortedLeaderboard.length}
									</div>
									<div className="text-[10px] text-text-tertiary uppercase tracking-wider">
										Participants
									</div>
								</div>
							</div>

							{/* Table Header */}
							<div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-divider/50 bg-bg-tertiary/10">
								<div className="col-span-2 sm:col-span-1 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">
									Rank
								</div>
								<div className="col-span-7 sm:col-span-8 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">
									Identity
								</div>
								<div className="col-span-3 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider text-right">
									Points
								</div>
							</div>

							{/* Content */}
							<div className="divide-y divide-divider/50">
								{isLeaderboardLoading ? (
									<div className="flex flex-col items-center justify-center py-16 gap-4">
										<Loader2 className="w-8 h-8 animate-spin text-brand" />
										<p className="text-sm text-text-tertiary">
											Loading leaderboard...
										</p>
									</div>
								) : leaderboardError ? (
									<div className="flex flex-col items-center justify-center py-16 gap-3">
										<div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
											<Trophy className="w-5 h-5 text-destructive" />
										</div>
										<p className="text-sm text-text-secondary">
											{leaderboardError}
										</p>
									</div>
								) : sortedLeaderboard.length === 0 ? (
									<div className="flex flex-col items-center justify-center py-16 gap-4">
										<div className="w-16 h-16 rounded-2xl bg-bg-tertiary/50 flex items-center justify-center">
											<Trophy className="w-7 h-7 text-text-tertiary" />
										</div>
										<div className="text-center">
											<p className="text-text-secondary font-medium">
												No rewards points yet
											</p>
											<p className="text-sm text-text-tertiary mt-1">
												Complete challenges to start earning points!
											</p>
										</div>
									</div>
								) : (
									sortedLeaderboard.map((entry, index) => (
										<div
											key={`${entry.displayName ?? entry.identity}-${entry.points}`}
											className={cn(
												"grid grid-cols-12 gap-4 px-5 py-4 transition-colors hover:bg-bg-tertiary/30",
												index < 3 && "bg-bg-tertiary/10",
											)}
										>
											<div className="col-span-2 sm:col-span-1 flex items-center">
												<RankBadge rank={index + 1} />
											</div>
											<div className="col-span-7 sm:col-span-8 flex items-center">
												<IdentityButton
													identityName={
														entry.displayName ?? entry.identity ?? ""
													}
												/>
											</div>
											<div className="col-span-3 flex items-center justify-end">
												<span
													className={cn(
														"font-semibold tabular-nums",
														index < 3
															? "text-text-default"
															: "text-text-secondary",
													)}
												>
													{entry.points.toLocaleString()}
												</span>
											</div>
										</div>
									))
								)}
							</div>
						</div>
					</TabsContent>

					{/* Staking Leaderboard */}
					<TabsContent value="staking" className="mt-0">
						<div className="rounded-2xl border border-divider bg-bg-secondary/20 backdrop-blur-sm overflow-hidden">
							{/* Header */}
							<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-divider bg-bg-tertiary/30">
								<div className="flex items-center gap-3">
									<div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center">
										<TrendingUp className="w-4 h-4 text-brand" />
									</div>
									<div>
										<h2 className="font-medium text-text-default">
											Staked KAI
										</h2>
										<p className="text-xs text-text-tertiary">
											Ranked by staking amount
										</p>
									</div>
								</div>
								{/* Search */}
								<div className="relative w-full sm:w-80">
									<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
									<input
										type="search"
										value={stakingFilter}
										onChange={(e) => handleStakingFilterChange(e.target.value)}
										placeholder="Search identity..."
										className="w-full bg-bg-secondary border border-divider rounded-lg py-2 pl-9 pr-3 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all"
									/>
								</div>
							</div>

							<IdentityList
								externalFilter={debouncedStakingFilter}
								hideSearch
							/>
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</main>
	);
}
